/* eslint-disable react/forbid-prop-types */
import React, { useEffect, useState } from 'react';
import {
  any,
  array,
  arrayOf,
  bool,
  func,
  number,
  objectOf,
  shape,
  string,
} from 'prop-types';
import { withRouter } from 'react-router-dom';
import { Typography } from '@mui/material';
import moment from 'moment-timezone';
import { eventNames, reportToSegment, types } from '@smartcar/morse';

import { initialState, PAGE_SIZE } from '../../reducers';
import { useDelay } from '../../../../../../hooks';
import FEATURES from '../../../../../../services/featureGate/features';
import { getLogRestrictions } from '../../../../utils';
import { LogTable, NoResults, Spinner } from '../../../../../../components';
import applicationText from '../../../../../../localization/Application/application';
import formatFilterValues from '../../../../../../utils/formatFilterValues';
import { ErrorsList, LogsContainer } from '../../styles';
import validateFilterValues from '../../utils/validateFilterValues';
import { FiltersBar } from './components';

const WebhookLogs = ({
  actions: { fetchWebhookEventsRequest, updateWebhookEventsFilterValues },
  webhookEvents: {
    pageInfo,
    pageToNextCursorMap,
    filterValues,
    paginationModel,
    errors,
    isFetching,
  },
  featureSetId,
  rolePermissions,
  location,
  history,
}) => {
  const urlParams = new URLSearchParams(location.search);
  const webhookId = urlParams.get('webhookId');
  const [formError, setFormError] = useState('');
  const { afterDelay: showLoading, resetTimer } = useDelay();
  const [openFiltersDrawer, setOpenFiltersDrawer] = useState(false);

  const logRestrictions = getLogRestrictions(featureSetId, FEATURES.LOG_DAYS);
  const { maxDate, defaultStartDate, minDate } = logRestrictions;
  const canWriteBilling = rolePermissions.includes('write_billing');

  const {
    webhookEvents: {
      paginationModel: defaultPagination,
      filterValues: defaultFilterValues,
    },
  } = initialState;

  const getPageInfo = (page, filters) => {
    const cursor = pageToNextCursorMap[page - 1];
    if (page === 0 || cursor) {
      const formattedFilterValues = {
        ...formatFilterValues(filters),
        limit: PAGE_SIZE,
        ...(cursor && { cursor }),
      };

      fetchWebhookEventsRequest({
        filterValues: formattedFilterValues,
        newPaginationModel: {
          page,
          pageSize: PAGE_SIZE,
        },
      });
    }
  };

  const handlePaginationModelChange = (newPaginationModel) => {
    getPageInfo(newPaginationModel.page, filterValues);
    const [label, text] =
      newPaginationModel.page > paginationModel.page
        ? ['forward', '[webhookLogs - right arrow icon]']
        : ['back', '[webhookLogs - left arrow icon]'];
    reportToSegment(types.TRACK, eventNames.buttonClicked, { label, text });
    resetTimer();
  };

  const errorsToDisplay = Object.entries(errors).filter(([, message]) => {
    if (!message) return false;
    return true;
  });

  if (formError) {
    errorsToDisplay.push(['formError', formError]);
  }

  const handleApplyFilters = (newFilterValues) => {
    const { error } = validateFilterValues(newFilterValues, logRestrictions);
    if (error) {
      setFormError(error);
      return;
    }
    setFormError('');
    getPageInfo(defaultPagination.page, newFilterValues);
    updateWebhookEventsFilterValues(newFilterValues);
    resetTimer();
    setOpenFiltersDrawer(false);
  };

  const handleResetAll = () => {
    const resetFilterValues = {
      ...defaultFilterValues,
      start: moment(defaultStartDate).utc(),
      end: moment(maxDate).utc(),
    };
    getPageInfo(defaultPagination.page, resetFilterValues);
    updateWebhookEventsFilterValues(resetFilterValues);
    resetTimer();
  };

  useEffect(() => {
    let initializedFilterValues = {
      ...filterValues,
      start: filterValues.start
        ? filterValues.start
        : moment(defaultStartDate).utc(),
      end: filterValues.end ? filterValues.end : moment(maxDate).utc(),
    };

    if (webhookId) {
      initializedFilterValues = {
        ...initializedFilterValues,
        webhookIdOrName: webhookId,
        start: moment(minDate).utc(),
        end: moment(maxDate).utc(),
      };

      // Remove webhookId from URL params to avoid confusion
      // since filterValues currently depend on redux state and not URL params
      urlParams.delete('webhookId');
      history.replace({
        pathname: location.pathname,
        search: urlParams.toString(),
      });
    }
    getPageInfo(paginationModel.page, initializedFilterValues);
    updateWebhookEventsFilterValues(initializedFilterValues);
  }, []);

  return (
    <LogsContainer>
      <FiltersBar
        filterValues={filterValues}
        logRestrictions={logRestrictions}
        handleApplyFilters={handleApplyFilters}
        handleResetAll={handleResetAll}
        setFormError={setFormError}
        canWriteBilling={canWriteBilling}
        openFiltersDrawer={openFiltersDrawer}
        setOpenFiltersDrawer={setOpenFiltersDrawer}
      />
      {errorsToDisplay.length > 0 && (
        <ErrorsList>
          {errorsToDisplay.map(([errorKey, message]) => {
            return (
              <li key={errorKey}>
                <Typography variant="caption" color="error">
                  {message}
                </Typography>
              </li>
            );
          })}
        </ErrorsList>
      )}

      {!pageInfo.rows && isFetching && <Spinner delay={200} />}

      {pageInfo.rows && pageInfo.rows.length > 0 && (
        <LogTable
          pageInfo={pageInfo}
          pageSizeOptions={[PAGE_SIZE]}
          paginationModel={paginationModel}
          handlePaginationModelChange={handlePaginationModelChange}
          loading={showLoading && isFetching}
        />
      )}

      {pageInfo.rows && pageInfo.rows.length === 0 && (
        <NoResults
          text={applicationText.paintedDoor.noData}
        />
      )}
    </LogsContainer>
  );
};

export default withRouter(WebhookLogs);

WebhookLogs.propTypes = {
  actions: shape({
    fetchWebhookEventsRequest: func.isRequired,
    updateWebhookEventsFilterValues: func.isRequired,
  }).isRequired,
  webhookEvents: shape({
    isFetching: bool.isRequired,
    filterValues: shape({
      end: any,
      start: any,
      limit: number.isRequired,
    }).isRequired,
    pageInfo: shape({
      rows: array.isRequired,
      columns: array.isRequired,
    }),
    paginationModel: shape({
      page: number,
      pageSize: number,
    }).isRequired,
    pageToNextCursorMap: objectOf(string),
    errors: shape({
      events: string,
    }),
  }).isRequired,
  featureSetId: string.isRequired,
  rolePermissions: arrayOf(string).isRequired,
  location: shape({
    search: string.isRequired,
  }).isRequired,
  history: shape({
    replace: func.isRequired,
  }).isRequired,
};
